import React, { useEffect, useLayoutEffect, useState } from "react";
import pluralize from "pluralize";

import SwitchButton from "src/components/switchButton";
import { TabBar } from "src/components/tabbar";

import { AssessmentParameter, AssessmentConfiguration, AssessmentSubParameter } from "src/types/coaching/simulation";

import _ from "lodash";
import cx from "classnames";
import styles from "./preview.module.scss";

interface ScoreCardPreviewProps {
  scorecard: AssessmentConfiguration;
  onClick: () => void;
  defaultScoreCard?: AssessmentConfiguration | null;
}

const ScoreCardPreview: React.FC<ScoreCardPreviewProps> = ({scorecard, onClick, defaultScoreCard }) => {

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const parameters = _.get(scorecard, 'parameters', []) as AssessmentParameter[];

  const [selectedParameter, setSelectedParameter] = useState<AssessmentParameter | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedSubParameter, setSelectedSubParameter] = useState<AssessmentSubParameter[]>([]);

  const isDefault = scorecard.id === defaultScoreCard?.id;

  useEffect(() => {
    if (_.size(parameters) > 0) {
      setSelectedParameter(parameters[0]);
    }
  }, [parameters]);

  useEffect(() => {
    if (selectedParameter?.parameters) {
      setSelectedSubParameter(selectedParameter.parameters);
      setActiveTab(0);
    }
  }, [selectedParameter]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
  
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [selectedSubParameter, activeTab]);
  
  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <div className={styles.header}>
          <i className={cx("paperflite-left-arrow", styles.backIcon)}
            onClick={onClick} />
          <div className={styles.title}>{scorecard.name}</div>
        </div>
      </div>
      <div className={styles.description}>{scorecard.description} </div>
      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.toggleButton}>
            <SwitchButton
              id="defaultScoreCard"
              label="set as default"
              className={cx(styles.switch, {
                [styles.active]: isDefault,
              })}
              checked={isDefault}
              disabled
            />
            <div className={styles.callType}>all types</div>
          </div>
          <div className={styles.parameters}>
            <div className={styles.title}>  {`parameters (${_.size(parameters)})`}</div>
            <div className={styles.parametersList}>
              {parameters.map((paramGroup, index) => {
                const subParamCount = paramGroup.parameters?.length || 0;

                return (
                  <div key={index} className={cx(
                    styles.paramGroup,
                    selectedParameter === paramGroup && styles.active
                  )} onClick={() => setSelectedParameter(paramGroup)}>
                    <div className={styles.paramGroupName}>{paramGroup.name}</div>
                    <div className={styles.meta}>
                      <span className={styles.item}>{subParamCount} {pluralize("sub-parameter", subParamCount)}</span>
                      <span className={styles.item}>Weightage: {paramGroup.weightage}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <div className={styles.info}>
            <div className={styles.inputValue}>
              <div className={styles.name}>Parameter name</div>
              <div className={styles.inputWrapper}>
                <input
                  className={styles.parameterNameInput}
                  type="text"
                  placeholder="Enter parameter name"
                  value={selectedParameter?.name || ""}
                  onChange={() => { }}
                  disabled={true}
                />
              </div>
            </div>
            <div className={styles.inputValue}>
              <div className={styles.name}>Weightage:</div>
              <div className={styles.inputWrapper}>
                <input
                  className={styles.weightageInput}
                  type="text"
                  placeholder="Enter weightage"
                  value={selectedParameter?.weightage || ""}
                  onChange={() => {}}
                  disabled={true}
                />
              </div>
            </div>
          </div>
          <div className={styles.subParameters}>
            <div className={styles.heading}>
            sub-{pluralize("parameter", selectedParameter?.parameters?.length || 0)} ({selectedParameter?.parameters?.length || 0})
            </div>
            <div className={styles.description}>Define the aspect of performance to be assessed for each sub-parameter.</div>
            <TabBar
              className={styles.tabs}
              gap={32}
              active={activeTab}
              onSelectionChange={(tabIndex) => setActiveTab(tabIndex)}
            >
              {selectedParameter?.parameters?.map((subParam, idx) => (
                <div key={idx} className={cx(styles.tabItem, {
                  [styles.activeTab]: idx === activeTab
                })}>{subParam.name}</div>
              ))}
            </TabBar>
            <div className={styles.details}>
              <div className={styles.subParamDetails}>
                Evaluates</div>
              <textarea
                ref={textareaRef}
                value={selectedSubParameter[activeTab]?.criteria || ""}
                className={styles.criteriaInput}
                disabled
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScoreCardPreview;